import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { usePlugins } from "@/plugins/usePlugins";
import { feedbackService } from "@/services/feedbackService";
import { apiError } from "@/lib/api";
import { FlaskConical, Bug, Lightbulb, X, Send, Loader2, Paperclip, Monitor } from "lucide-react";

const TYPE_OPTS = [
  { k: "bug", label: "Bug", icon: Bug, sel: "border-rose-500 bg-rose-500/10 text-rose-600" },
  { k: "inspiration", label: "Enhancement", icon: Lightbulb, sel: "border-amber-500 bg-amber-500/10 text-amber-600" },
];

const POS_KEY = "lm_beta_pos";
const PANEL_KEY = "lm_beta_panel_pos";
const SIZE = 56;

export function BetaCompanion() {
  const { user } = useAuth();
  const { isPluginEnabled } = usePlugins();
  const [pos, setPos] = useState(() => {
    try {
      const p = JSON.parse(localStorage.getItem(POS_KEY));
      if (p && typeof p.x === "number" && typeof p.y === "number") return p;
    } catch {}
    return null; // null → default bottom-right
  });
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("bug");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const drag = useRef({ dragging: false, moved: false, ox: 0, oy: 0 });
  const btnRef = useRef(null);
  const fileRef = useRef(null);
  const [panelPos, setPanelPos] = useState(() => {
    try {
      const p = JSON.parse(localStorage.getItem(PANEL_KEY));
      if (p && typeof p.x === "number" && typeof p.y === "number") return p;
    } catch {}
    return null; // null → default anchored bottom-right
  });
  const panelDrag = useRef({ dragging: false, ox: 0, oy: 0 });
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!user || !user.is_tester || !isPluginEnabled("feedback")) return null;

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Screenshot too large (max 5 MB)"); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };
  const clearFile = () => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null); setPreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };
  const captureScreen = async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      toast.error("Screen capture isn't supported in this browser — attach a file instead");
      return;
    }
    setCapturing(true); // hide our modal so it isn't in the shot
    let stream;
    try {
      await new Promise((r) => setTimeout(r, 150));
      stream = await navigator.mediaDevices.getDisplayMedia({ video: { displaySurface: "browser" }, audio: false });
      const track = stream.getVideoTracks()[0];
      const video = document.createElement("video");
      video.srcObject = stream;
      await video.play();
      await new Promise((r) => setTimeout(r, 300));
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext("2d").drawImage(video, 0, 0);
      track.stop();
      await new Promise((resolve) => canvas.toBlob((blob) => {
        if (blob) {
          if (preview) URL.revokeObjectURL(preview);
          setFile(new File([blob], "screen-capture.png", { type: "image/png" }));
          setPreview(URL.createObjectURL(blob));
          toast.success("Screen captured");
        }
        resolve();
      }, "image/png"));
    } catch (e) {
      if (stream) stream.getTracks().forEach((t) => t.stop());
      // silent on user cancel
    } finally {
      setCapturing(false);
    }
  };

  const onPointerDown = (e) => {
    const rect = btnRef.current.getBoundingClientRect();
    drag.current = { dragging: true, moved: false, ox: e.clientX - rect.left, oy: e.clientY - rect.top };
    try { btnRef.current.setPointerCapture(e.pointerId); } catch {}
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d.dragging) return;
    if (Math.abs(e.movementX) + Math.abs(e.movementY) > 2) d.moved = true;
    const x = Math.max(4, Math.min(window.innerWidth - SIZE - 4, e.clientX - d.ox));
    const y = Math.max(4, Math.min(window.innerHeight - SIZE - 4, e.clientY - d.oy));
    setPos({ x, y });
  };
  const onPointerUp = (e) => {
    const d = drag.current;
    if (!d.dragging) return;
    d.dragging = false;
    try { btnRef.current.releasePointerCapture(e.pointerId); } catch {}
    if (d.moved) {
      setPos((p) => { if (p) { try { localStorage.setItem(POS_KEY, JSON.stringify(p)); } catch {} } return p; });
    } else {
      setOpen(true);
    }
  };

  const submit = async () => {
    if (!title.trim()) { toast.error("Please enter a title"); return; }
    if (!message.trim()) { toast.error("Please enter a description"); return; }
    setSubmitting(true);
    try {
      await feedbackService.submit({
        type,
        title: title.trim(),
        message: message.trim(),
        url: window.location.href,
        user_agent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        screenshot: file,
      });
      toast.success(type === "bug" ? "Bug reported — thank you!" : "Sent to the team — thank you!");
      setTitle("");
      setMessage("");
      clearFile();
      setOpen(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const style = pos ? { left: pos.x, top: pos.y } : undefined;

  const onPanelDown = (e) => {
    const rect = panelRef.current.getBoundingClientRect();
    panelDrag.current = { dragging: true, ox: e.clientX - rect.left, oy: e.clientY - rect.top };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
  };
  const onPanelMove = (e) => {
    const d = panelDrag.current;
    if (!d.dragging) return;
    const el = panelRef.current;
    const w = el.offsetWidth, h = el.offsetHeight;
    const x = Math.max(4, Math.min(window.innerWidth - w - 4, e.clientX - d.ox));
    const y = Math.max(4, Math.min(window.innerHeight - h - 4, e.clientY - d.oy));
    setPanelPos({ x, y });
  };
  const onPanelUp = (e) => {
    const d = panelDrag.current;
    if (!d.dragging) return;
    d.dragging = false;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
    setPanelPos((p) => { if (p) { try { localStorage.setItem(PANEL_KEY, JSON.stringify(p)); } catch {} } return p; });
  };
  const panelStyle = panelPos ? { left: panelPos.x, top: panelPos.y } : undefined;

  return (
    <>
      <button
        ref={btnRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        data-testid="beta-companion-button"
        title="Test Buddy — drag to move, click to open"
        className={`fixed z-[95] h-14 w-14 rounded-full flex items-center justify-center text-white shadow-[0_10px_30px_-6px_rgba(124,58,237,0.7)] touch-none select-none cursor-grab active:cursor-grabbing ${pos ? "" : "right-6 bottom-24 xl:right-[324px]"}`}
        style={{ ...(style || {}), background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)" }}
      >
        <FlaskConical className="h-6 w-6 pointer-events-none" />
        <span className="absolute -top-1 -right-1 bg-white text-[8px] font-bold uppercase tracking-wide text-indigo-700 px-1.5 py-0.5 rounded-full shadow pointer-events-none">
          Test
        </span>
      </button>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              ref={panelRef}
              initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.98 }}
              className={`fixed z-[95] w-[360px] max-w-[92vw] max-h-[85vh] overflow-y-auto bg-card border border-border rounded-2xl shadow-2xl ${panelPos ? "" : "right-6 bottom-24 xl:right-[324px]"} ${capturing ? "opacity-0 pointer-events-none" : ""}`}
              style={panelStyle}
              data-testid="beta-feedback-modal"
            >
                <div
                  className="flex items-center justify-between px-5 py-4 border-b border-border cursor-grab active:cursor-grabbing touch-none select-none"
                  style={{ background: "linear-gradient(135deg, rgba(124,58,237,0.12), rgba(79,70,229,0.06))" }}
                  onPointerDown={onPanelDown}
                  onPointerMove={onPanelMove}
                  onPointerUp={onPanelUp}
                  data-testid="beta-panel-drag-handle"
                >
                  <div className="flex items-center gap-2">
                    <FlaskConical className="h-5 w-5 text-indigo-600" />
                    <h3 className="font-display text-lg font-extrabold tracking-tight">Test Buddy</h3>
                  </div>
                  <button onClick={() => setOpen(false)} onPointerDown={(e) => e.stopPropagation()} data-testid="beta-close" className="text-muted-foreground hover:text-foreground transition-colors">
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="p-5 space-y-4">
                  <p className="text-sm text-muted-foreground">Spotted something or had an idea? File it straight into the Change board. Drag the header to move me aside.</p>

                  <div className="grid grid-cols-2 gap-2">
                    {TYPE_OPTS.map((t) => {
                      const TIcon = t.icon;
                      return (
                        <button
                          key={t.k}
                          onClick={() => setType(t.k)}
                          data-testid={`beta-type-${t.k}`}
                          className={`flex flex-col items-center justify-center gap-1 h-16 border font-semibold text-xs transition-colors rounded-xl ${type === t.k ? t.sel : "border-border hover:border-foreground"}`}
                        >
                          <TIcon className="h-4 w-4" /> {t.label}
                        </button>
                      );
                    })}
                  </div>

                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Title — a short summary"
                    maxLength={120}
                    data-testid="beta-title-input"
                    className="w-full border border-input bg-background rounded-xl px-3 py-2.5 text-sm font-semibold outline-none focus:border-primary transition-colors"
                  />

                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={type === "bug" ? "Description — what went wrong? Steps to reproduce help a lot." : "Description — share your idea or thought about Learn Me…"}
                    rows={4}
                    data-testid="beta-message-input"
                    className="w-full border border-input bg-background rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary transition-colors resize-none"
                  />

                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={pickFile} className="hidden" data-testid="beta-screenshot-input" />
                  {preview ? (
                    <div className="relative inline-block">
                      <img src={preview} alt="screenshot preview" className="max-h-28 rounded-lg border border-border" data-testid="beta-screenshot-preview" />
                      <button onClick={clearFile} data-testid="beta-screenshot-remove"
                        className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-foreground text-background flex items-center justify-center shadow">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => fileRef.current?.click()} data-testid="beta-screenshot-attach"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground border border-dashed border-border rounded-xl px-3 h-10 transition-colors">
                        <Paperclip className="h-4 w-4" /> Attach screenshot
                      </button>
                      <button onClick={captureScreen} disabled={capturing} data-testid="beta-capture-screen"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-700 border border-indigo-300 rounded-xl px-3 h-10 transition-colors disabled:opacity-60">
                        {capturing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Monitor className="h-4 w-4" />} Capture screen
                      </button>
                    </div>
                  )}

                  <p className="text-[11px] text-muted-foreground truncate">
                    Attaching: <span className="font-mono">{window.location.pathname}</span> · {window.innerWidth}×{window.innerHeight}
                  </p>

                  <button
                    onClick={submit}
                    disabled={submitting}
                    data-testid="beta-submit"
                    className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl font-display font-bold tracking-tight text-white disabled:opacity-60 transition-opacity"
                    style={{ background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)" }}
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {submitting ? "Sending…" : "Send feedback"}
                  </button>
                </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
